import router from 'source/router';
import axios from 'source/lib/axios';
import sileo from 'sileo';
import plex from 'core/js/restmodel';
import idb from 'idb';
import store from 'source/store';

// Indexed DB collections
const BOARD_CONTEXT_FILTERS = 'board_context_filters';
const MILESTONES_CACHED_COLLECTION = 'idb_milestones_cahed';
const OVERVIEWS_CACHED_COLLECTION = 'idb_overview_tiles_cached';
// Added this idb for Scalema New Modal query
const SCALEMA_NEW_QUERY = 'scalema_client_query';

// const SignUp = new sileo.Model('scalema_new', 'scalema-sign-up');

const state = {
    user: null,
    clientUserPermissions: null,
    userWorkforcePermissions: null,
    token: null,
    selectedCompany: null,
    refreshToken: null,
    userRole: '',
    redirectionToken: null,
    userEmail: '',
    userWorkforceRecruitmentBoardPermissions: null,
    currentWorkforcePk: null,
};

const getters = {
    userRole: state => state.userRole,

    token: state => state.token,

    refreshToken: state => state.refreshToken,

    user: state => state.user,

    userEmail: state => state.userEmail,

    clientUserPermissions(state) {
        if (state.user) {
            return state.user.user_profile.client_workforce_permissions;
        }
        return {};
    },

    userWorkforcePermissions(state) {
        if (state.user &&
            (state.user.user_profile.is_organizational_exec
                || state.user.user_profile.is_workforce_exec)) {
            return state.user.user_profile.permissions;
        }
        return state.userWorkforcePermissions;
    },

    isLoggedIn(state) {
        return state.user !== null;
    },

    userType(state) {
        if (state.user)
            return state.user.user_profile.user_type;
        return null;
    },
    userRegion(state) {
        if (state.user && state.user.user_profile) {
            return state.user.user_profile.region;
        }
        return null;
    },

    userRegionSchedule(state) {
        if (state.user && state.user.user_profile) {
            return state.user.user_profile?.region;
        }
        return null;
    },

    companies(state) {
        if (!state.user) {
            return [];
        }

        return state.user.user_profile.companies;
    },

    redirectionToken: state => state.redirectionToken,

    userWorkforceRecruitmentBoardPermissions: state => state.userWorkforceRecruitmentBoardPermissions
};

const mutations = {
    setUser(state, user) {
        state.user = user;

        // save is_new_business_user flag into localStorage for ease of guarding routes
        let is_new_business_user = JSON.parse(localStorage.getItem('is_new_business_user', null));
        if (state.user.user_profile.is_fully_verified) {
            localStorage.setItem('is_new_business_user', false);
        } else if (!is_new_business_user) {
            is_new_business_user = !state.user.user_profile.is_fully_verified
                && state.user.user_profile.signup_source_display === 'AI Signup Funnel';
            localStorage.setItem('is_new_business_user', is_new_business_user);
        }
        // Set userWorkforceRecruitmentBoardPermissions data when page is refreshed or user data is mutated
        if (state.user && state.user.user_profile) {
            const userRecruitmentBoardPermissions = state.user.user_profile.recruitment_board_permissions;

            if (!userRecruitmentBoardPermissions) return;

            // The first data from the recruitment permissions list is assigned as the initial state
            // However, it is only assigned if the user is an exec of the current workforce.
            // Otherwise, the user's recruitment permissions is empty
            const keys = Object.keys(userRecruitmentBoardPermissions);
            const firstKeys = keys[0];

            if (!keys.length) {
                state.userWorkforceRecruitmentBoardPermissions = [];
                return;
            }

            const firstRecruitmentPermissionList = userRecruitmentBoardPermissions[firstKeys];

            if (state.currentWorkforcePk) {
                const currentWorkforcePk = state.currentWorkforcePk.toString();
                state.userWorkforceRecruitmentBoardPermissions = keys.includes(currentWorkforcePk)
                    ? firstRecruitmentPermissionList : [];
            } else {
                state.userWorkforceRecruitmentBoardPermissions = firstRecruitmentPermissionList;
            }
        }
    },

    setUserAvatar(state, avatarURL) {
        state.user.user_profile.avatar_url = avatarURL;
    },

    setUserEmail(state, email) {
        state.userEmail = email;
        localStorage.removeItem('truncated_email');
    },

    setUserWorkforcePermissions(state, userWorkforcePermissions) {
        state.userWorkforcePermissions = userWorkforcePermissions;
    },

    clearUser(state) {
        state.user = null;
    },

    setToken(state, token) {
        if (token) {
            state.token = token.token;
            state.refreshToken = token.refresh;
            axios.defaults.headers.common['Authorization'] = token.token;
            axios.defaults.headers.common['X-Refresh-Token'] = token.refresh;
            localStorage.setItem('refresh_token', token.refresh);
            localStorage.setItem('auth_token', token.token);
            sileo.defaults.headers['Authorization'] = token.token;
            sileo.defaults.headers['X-Refresh-Token'] = token.refresh;

            // cache user name and email in local storage
            const userData = {
                first_name: state.user.first_name,
                last_name: state.user.last_name,
                email: state.user.email,
            };
            localStorage.setItem('scalema_user_data', JSON.stringify(userData));
        }
    },

    clearToken(state) {
        state.token = null;
        delete axios.defaults.headers.common['Authorization'];
        delete axios.defaults.headers.common['X-Refresh-Token'];
        localStorage.removeItem('scalema_user_data');
    },

    clearLocalStorage() {
        const pinnedData = {};
        const keys = ['dismissed-id'];
        const prefixes = ['overview-state-'];

        for (const key of Object.keys(localStorage)) {
            if (keys.includes(key) || prefixes.some(prefix => key.startsWith(prefix))) {
                pinnedData[key] = localStorage.getItem(key);
            }
        }

        localStorage.clear();

        // Restore only the whitelisted keys so data persists across user logins
        Object.entries(pinnedData).forEach(([key, value]) => {
            localStorage.setItem(key, value);
        });
    },

    setSelectedCompany(state, company) {
        state.selectedCompany = company;
    },

    clearSelectedCompany(state) {
        state.selectedCompany = null;
    },

    setUserRole(state, userRole) {
        state.userRole = userRole;
    },

    setRedirectionToken(state, redirectionToken) {
        state.redirectionToken = redirectionToken;
    },

    setUserWorkforceRecruitmentBoardPermissions(state, userWorkforceRecruitmentBoardPermissions) {
        state.userWorkforceRecruitmentBoardPermissions = userWorkforceRecruitmentBoardPermissions;
    },

    setCurrentWorkforcePk(state, workforcePk) {
        state.currentWorkforcePk = workforcePk;
    }
};

const actions = {
    performScalemaSignup(context, payload) {
        const form = new FormData();
        Object.keys(payload).forEach((key) => {
            form.append(key, payload[key]);
        });

        return new Promise((resolve, reject) => {
            axios.post('/scalema-sign-up/', form)
                .then(response => {
                    if (!response.error) {
                        resolve(response);
                    } else {
                        reject(new Error(response.error));
                    }
                }).catch(e => {
                    reject(new Error(e.response.data.__all__));
                });
        });
    },


    performBusinessSignup(context, payload) {
        const form = new FormData();
        Object.keys(payload).forEach((key) => {
            form.append(key, payload[key]);
        });
        return new Promise((resolve, reject) => {
            axios.post('/new-signup/', form)
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response.data.email) {
                        reject(error.response.data);
                    } else {
                        reject(error);
                    }
                });
        });
    },

    performLogin({ commit }, data) {
        let formData = new FormData();
        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        return new Promise((resolve, reject) => {
            axios.post('/hqzen-signin/', formData).then(response => {
                if (response.tfa_token) {
                    localStorage.setItem('tfa_token', response.tfa_token);
                    commit('setUserEmail', response.email);
                    resolve(response);
                } else if (response.user && Object.keys(response.user).length) {
                    commit('setUser', response.user);
                    commit('setToken',{ 'token': response.access_token, 'refresh': response.refresh_token });
                    if (localStorage.getItem('auth_token')) {
                        sileo.defaults.headers['Authorization'] = localStorage.getItem('auth_token');
                    }
                    if (localStorage.getItem('refresh_token')) {
                        sileo.defaults.headers['X-Refresh-Token'] = localStorage.getItem('refresh_token');
                    }
                } else {
                    localStorage.setItem('tfa_token', response.tfa_token);
                }
                // automatically handles cases when response.error
                resolve(response);
                setTimeout(() => {
                    idb.reset().catch(e => console.error(e));
                }, 100);
                // deletes the idb of scalema new query stored from previous user upon log in
            }).catch(error => {
                console.error(error.response);
                reject(error);
            });
        });
    },

    performPasswordValidation(context, data) {
        let formData = new FormData();
        Object.keys(data).forEach(key => {
            formData.append(key, data[key]);
        });

        return new Promise((resolve, reject) => {
            axios.post('/hqzen-validate-password/', formData).then(response => {
                resolve(response);
            }).catch(error => {
                console.error(error.response);
                reject(error.response.data.error);
            });
        });
    },

    performPasswordChange(context, data) {
        return axios.post('/password-change/', {
            data: data,
        }).catch(response => {
            console.error(response);
        });
    },

    performPasswordReset(context, data) {
        return axios.post('/password-reset/', {
            data: data,
        }).catch(response => {
            console.error(response);
        });
    },

    performGetTFA() {
        return axios.get('/api-tfa/', {
            params: {
                token: localStorage.getItem('tfa_token')
            } }).catch(response => {
            console.error(response);
        });
    },

    performPostTFA({ commit }, data) {
        return axios.post('/api-tfa/', {
            code: data,
            token: localStorage.getItem('tfa_token')
        }).then(response => {
            if (response.user && response.token) {
                console.log(response.token);
                commit('setUser', response.user);
                commit('setToken', response);
            }
            delete sileo.defaults.headers['tfa_token'];
            return response;
        }).catch(response => {
            console.error(response);
        });
    },

    performResendTFA() {
        return axios.get('/api-resend-tfa/', {
            params: {
                token: localStorage.getItem('tfa_token')
            }
        }).then(response => {
            localStorage.setItem('tfa_token', response.tfa_token);
            return response;
        }).catch(response => {
            console.error(response);
            return response;
        });
    },

    performResendTFAVerificationEmail(context, data) {
        return axios.get('/resend-tfa-verification-email/', {
            params: {
                email: data
            }
        }).then(response => {
            localStorage.setItem('tfa_token', response.tfa_token);
            return response;
        });
    },

    async performLogout({ dispatch }, forced=false) {
        if (!forced) {
            try {
                await axios.get('/hqzen-signout/');
            } catch (error) {
                console.error(error);
            }
        }

        const route = router.currentRoute.value;
        if (
            route?.path.includes('/help-center/')
            || route.name === 'operations-form'
            || route.name === 'engagement-form'
            || route.name === 'shareable-form-answer'
        ) {
            router.push({ path: route.path });
        } else if (forced) {
            router.replace({
                name: 'signin',
                params: {
                    isForced: forced
                }
            });
        } else {
            router.replace({ name: 'landing-page' });
        }

        dispatch('clearData');
    },

    async getCurrentUser({ commit, dispatch }) {
        return await axios.get(
            '/me/'
        ).then(response => {
            if (response.username) {
                commit('setUser', response);
                localStorage.setItem('is-on-admin-leave',
                    response.user_profile.employment.is_on_admin_leave);
            } else if (response.message.includes('not authenticated')) {
                return null;
            } else {
                return null;
            }
            return response;
        }).catch(function (error) {
            return null;
        });
    },

    async getWebsocketToken( state ) {
        if (state.user) {
            let resp = await axios.get('/websocket-token/');
            return resp.token;
        }

        return null;
    },

    /*
    * TODO (jace@bposeats.com): Remove when all ApplyBPO.com features
    * have been ported over to HQZen.com.
    */
    retrieveApplyBPORedirectionToken({ commit }) {
        return axios.get('/redirection-token/')
            .then(data => {
                commit('setRedirectionToken', data.token);
                return data.token;
            }).catch(error => error);
    },

    setSelectedCompany({ commit }, data) {
        commit('setSelectedCompany', data);
    },

    authenticateUsingRedirectionToken(context, data) {
        return axios.post('/redirection-signin/', data)
            .then(response => {

                context.commit('setUser', response.user);
                context.commit('setToken', { 'token': response.token, 'refresh_token': response.refresh_token });

                return response;
            }).catch(error => {
                console.error(error);
                return error;
        });
    },

    setUserRole({ commit }, data) {
        commit('setUserRole', data);
    },

    async clearData({ commit, state }) {
        /**
         * Clear all data
         * typically used in performLogout
        */

        // clear local storage
        commit('clearUser');
        commit('clearToken');
        commit('clearSelectedCompany');
        commit('clearLocalStorage');
        commit('workforce/clearWorkforces', null, { root: true });
        commit('employment/clearEmployment', null, { root: true });
        commit('employers/resetEmployerStates', null, { root: true });
        commit('hrPortal/clearCareerPolicies', null, { root: true });

        await store.dispatch('generics/closeWebsocketConnection');

        // delete headers
        delete axios.defaults.headers.common['X-Refresh-Token'];
        delete axios.defaults.headers.common['Authorization'];
        delete sileo.defaults.headers['X-Refresh-Token'];
        delete sileo.defaults.headers['Authorization'];
        delete plex.defaults.headers['X-Refresh-Token'];
        delete plex.defaults.headers['Authorization'];

        // Delete Indexed DB cahed data
        (() => {
            idb.collection(BOARD_CONTEXT_FILTERS)
                .delete()
                .then(res => {
                    return Promise.resolve(res);
                })
                .then(() => {
                    return idb.collection(MILESTONES_CACHED_COLLECTION)
                        .delete();
                })
                .then(() => {
                    return idb.collection(SCALEMA_NEW_QUERY)
                        .delete();
                })
                .then(() => {
                    return idb.collection(OVERVIEWS_CACHED_COLLECTION)
                        .delete();
                })
                .catch(e => {
                    console.log('Board filters drop error', e);
                });
        })();
        // Delete Indexed DB cahed data
        setTimeout(() => {
            idb.reset().catch(e => console.error(e));
        }, 100);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
